import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Heart,
} from "lucide-react";

const ImageViewer = ({
  isOpen,
  onClose,
  initialImage,
  images,
  currentIndex,
  setCurrentIndex,
}) => {
  // helpers to map imported asset URLs to the resized files in public/resized
  const getBaseName = (url) => {
    if (!url || typeof url !== "string") return null;
    const m = url.match(/([^\/]+?)(?:\.[0-9a-f]{6,})?\.(png|jpe?g|webp)$/i);
    if (m) return m[1];
    return null;
  };

  const buildSrcSet = (url, ext = "webp") => {
    const base = getBaseName(url);
    if (!base) return null;
    const sizes = [480, 800, 1200];
    return sizes.map((s) => `/resized/${base}-${s}.${ext} ${s}w`).join(", ");
  };
  const [zoomLevel, setZoomLevel] = useState(1);
  const [liked, setLiked] = useState(false);

  // Reset zoom level when changing images
  useEffect(() => {
    setZoomLevel(1);
  }, [currentIndex]);

  // Reset zoom level when changing images
  useEffect(() => {
    setZoomLevel(1);
  }, [currentIndex]);

  if (!isOpen || !images || images.length === 0) return null;

  const currentImage = images[currentIndex];

  const handlePrevious = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleZoomIn = (e) => {
    e.stopPropagation();
    setZoomLevel((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = (e) => {
    e.stopPropagation();
    setZoomLevel((prev) => Math.max(prev - 0.25, 1));
  };

  const handleLike = (e) => {
    e.stopPropagation();
    setLiked(!liked);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3 },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.3 },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        delay: 0.1,
        duration: 0.4,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      transition: { duration: 0.3 },
    },
  };

  const detailsVariants = {
    hidden: { opacity: 0, x: 30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        delay: 0.2,
        duration: 0.4,
      },
    },
    exit: {
      opacity: 0,
      x: 30,
      transition: { duration: 0.3 },
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={onClose}
        >
          {/* Backdrop with blur */}
          <div className="absolute inset-0 bg-primary-dark bg-opacity-85 backdrop-blur-sm"></div>

          {/* Close button (larger touch area for mobile) */}
          <motion.button
            aria-label="Cerrar visor"
            className="absolute top-3 right-3 md:top-4 md:right-4 z-50 p-3 md:p-2 rounded-full bg-primary-dark bg-opacity-70 text-black hover:bg-opacity-90 transition-colors duration-300 shadow-lg"
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.95 }}
            onClick={onClose}
          >
            <X className="w-6 h-6" />
          </motion.button>

          {/* Navigation buttons */}
          <motion.button
            className="absolute left-4 top-1/2 transform -translate-y-1/2 z-40 p-3 rounded-full bg-primary-dark bg-opacity-70 text-accent-light hover:bg-opacity-90 transition-colors duration-300 shadow-lg"
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.95 }}
            onClick={handlePrevious}
          >
            <ChevronLeft className="w-6 h-6" />
          </motion.button>

          <motion.button
            className="absolute right-4 top-1/2 transform -translate-y-1/2 z-40 p-3 rounded-full bg-primary-dark bg-opacity-70 text-accent-light hover:bg-opacity-90 transition-colors duration-300 shadow-lg"
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleNext}
          >
            <ChevronRight className="w-6 h-6" />
          </motion.button>

          {/* Main content container */}
          <div
            className="container relative z-10 mx-auto px-4 flex flex-col md:flex-row items-center md:items-stretch gap-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image container */}
            <motion.div
              className="w-full md:w-2/3 overflow-hidden rounded-2xl bg-black flex items-center justify-center relative shadow-2xl"
              variants={contentVariants}
            >
              <div className="relative w-full h-[60vh] md:h-[80vh] overflow-hidden flex items-center justify-center">
                <picture>
                  {(() => {
                    const webpSet = buildSrcSet(currentImage.image, "webp");
                    const jpgFallback = (() => {
                      const b = getBaseName(currentImage.image);
                      return b ? `/resized/${b}-1200.jpg` : currentImage.image;
                    })();
                    return (
                      <>
                        {webpSet && (
                          <source
                            type="image/webp"
                            srcSet={webpSet}
                            sizes="(max-width: 640px) 90vw, (max-width: 1024px) 80vw, 60vw"
                          />
                        )}
                        <img
                          src={jpgFallback}
                          alt={currentImage.title}
                          loading="eager"
                          decoding="async"
                          className="w-full h-full object-contain transition-transform duration-200 ease-out"
                          style={{
                            transform: `scale(${zoomLevel})`,
                            cursor: zoomLevel > 1 ? "grab" : "default",
                          }}
                        />
                      </>
                    );
                  })()}
                </picture>
              </div>

              {/* Zoom controls */}
              <div className="absolute bottom-4 left-4 flex space-x-3">
                <motion.button
                  className="p-2 rounded-full bg-primary-dark bg-opacity-80 text-accent-light hover:bg-opacity-95 transition-colors duration-300 shadow-lg"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 1}
                >
                  <ZoomOut className="w-5 h-5" />
                </motion.button>

                <motion.button
                  className="p-2 rounded-full bg-primary-dark bg-opacity-80 text-accent-light hover:bg-opacity-95 transition-colors duration-300 shadow-lg"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 3}
                >
                  <ZoomIn className="w-5 h-5" />
                </motion.button>
              </div>

              {/* Image counter */}
              <div className="absolute bottom-4 right-4 px-3 py-1 rounded-full bg-primary-dark bg-opacity-80 text-xs text-accent-light shadow-lg">
                {currentIndex + 1} / {images.length}
              </div>
            </motion.div>

            {/* Details panel */}
            <motion.div
              className="w-full md:w-1/3 bg-primary-light bg-opacity-90 backdrop-blur-sm rounded-2xl p-6 h-auto md:h-[80vh] flex flex-col overflow-y-auto shadow-2xl"
              variants={detailsVariants}
            >
              <div className="flex items-start justify-between mb-6">
                <h2 className="text-3xl font-bold text-accent-light">
                  {currentImage.title}
                </h2>
                <motion.button
                  className="p-2 rounded-full bg-primary-dark bg-opacity-40 text-accent-light hover:bg-opacity-60 transition-colors duration-300"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleLike}
                ></motion.button>
              </div>

              <div className="space-y-6 flex-1">
                {currentImage.description && (
                  <div className="bg-primary-darker bg-opacity-50 backdrop-blur-sm p-4 rounded-xl shadow-inner">
                    <h3 className="text-lg font-semibold text-accent mb-2">
                      Descripción
                    </h3>
                    <p className="text-accent-light text-opacity-90 leading-relaxed">
                      {currentImage.description}
                    </p>
                  </div>
                )}

                <div className="bg-primary-darker bg-opacity-50 backdrop-blur-sm p-4 rounded-xl shadow-inner">
                  <h3 className="text-lg font-semibold text-accent mb-2">
                    Detalles de la Obra
                  </h3>
                  <dl className="grid grid-cols-2 gap-2">
                    {currentImage.technique && (
                      <>
                        <dt className="text-accent-light text-opacity-70">
                          Técnica:
                        </dt>
                        <dd className="text-accent-light font-medium">
                          {currentImage.technique}
                        </dd>
                      </>
                    )}

                    {currentImage.dimensions && (
                      <>
                        <dt className="text-accent-light text-opacity-70">
                          Dimensiones:
                        </dt>
                        <dd className="text-accent-light font-medium">
                          {currentImage.dimensions}
                        </dd>
                      </>
                    )}

                    {currentImage.year && (
                      <>
                        <dt className="text-accent-light text-opacity-70">
                          Año:
                        </dt>
                        <dd className="text-accent-light font-medium">
                          {currentImage.year}
                        </dd>
                      </>
                    )}

                    {currentImage.editions && (
                      <>
                        <dt className="text-accent-light text-opacity-70">
                          Edición:
                        </dt>
                        <dd className="text-accent-light font-medium">
                          Serie de {currentImage.editions}
                        </dd>
                      </>
                    )}

                    {currentImage.category && (
                      <>
                        <dt className="text-accent-light text-opacity-70">
                          Categoría:
                        </dt>
                        <dd className="text-accent-light font-medium">
                          {currentImage.category}
                        </dd>
                      </>
                    )}
                  </dl>
                </div>

                {currentImage.story && (
                  <div className="bg-primary-darker bg-opacity-50 backdrop-blur-sm p-4 rounded-xl shadow-inner">
                    <h3 className="text-lg font-semibold text-accent mb-2">
                      Historia
                    </h3>
                    <p className="text-accent-light text-opacity-90 leading-relaxed">
                      {currentImage.story}
                    </p>
                  </div>
                )}

                {currentImage.tags && currentImage.tags.length > 0 && (
                  <div className="bg-primary-darker bg-opacity-50 backdrop-blur-sm p-4 rounded-xl shadow-inner">
                    <h3 className="text-lg font-semibold text-accent mb-2">
                      Etiquetas
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {currentImage.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 text-sm bg-accent bg-opacity-20 text-accent-light rounded-full shadow-sm"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {currentImage.price && (
                <div className="mt-6 pt-6 border-t border-accent border-opacity-20">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-accent">
                      {currentImage.price}
                    </span>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ImageViewer;
